import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import { Form } from 'semantic-ui-react';
import { ATTRIBUTE_TYPE, ATTRIBUTE_TYPE_INPUT_TYPE_MAPPING } from '../../../../HelperMethods/FormUtils';
import { getPriorityList, getPriorityTemplateSpecs } from '../../../../HelperMethods/TaskUtilities';
import { parseJSONStringToObject } from '../../../../HelperMethods/UtilityFunctions';
import * as prioritizationModelActions from '../../../prioritizationConfig/prioritizationModel/redux/actions';

// Style objects outside the component to prevent recreation on every render
const readonlyMessageStyle = {
  fontSize: '12px',
  color: '#666',
  marginTop: '5px',
};

const useGetPriorityOptions = (taskType, task) => {
  const taskList = useSelector((state) => state.taskViewPageReducer.tasks.items);
  let priorityTemplate = _.get(taskType, 'priorityTemplate');
  let priorityAlias = _.get(taskType, 'priorityAlias');
  if (_.get(task, 'parentTaskId')) {
    const reqParentTask = taskList.find((t) => t.id === task.parentTaskId);
    priorityTemplate = _.get(reqParentTask, 'taskType.priorityTemplate');
    priorityAlias = _.get(reqParentTask, 'taskType.priorityAlias');
  }
  const { basePriority, maxPriority } = getPriorityTemplateSpecs(priorityTemplate);

  const priorityListOptions = getPriorityList(basePriority, maxPriority, priorityAlias);

  return priorityListOptions;
};

const PriorityForTask = (props) => {
  const { attributeObj, disabled, formik, taskType, task } = props;
  const dispatch = useDispatch();

  // Score prioritization feature logic
  const settings = useSelector((state) => state.appReducer.settings) || {};
  const tenantId = useSelector((state) => state.appReducer.tenantId);
  const fieldId = useSelector((state) => state.appReducer.selectedField?.id);
  const allModels = useSelector((state) => state.prioritizationModelReducer?.models) || [];
  const activeModel = allModels.find((model) => model.isActive) || null;

  // Load prioritization models when component mounts
  useEffect(() => {
    if (tenantId && fieldId && allModels.length === 0) {
      dispatch(prioritizationModelActions.loadModelsAndFactors(tenantId, fieldId));
    }
  }, [dispatch, tenantId, fieldId, allModels.length]);

  const isScorePrioritizationEnabled = settings?.featureFlags?.enableScoreFeature ?? false;
  const shouldPriorityBeReadonly = isScorePrioritizationEnabled && !!activeModel;

  const savedPriorityVal = _.get(formik.values, attributeObj.attributeName);
  const attributeOnChange = (event, data) => {
    if (!shouldPriorityBeReadonly) {
      formik.setFieldValue(attributeObj.attributeName, data.value);
    }
  };
  let priorityListOptions = useGetPriorityOptions(taskType, task);
  const priorityAlias = parseJSONStringToObject(_.get(taskType, 'priorityAlias'));

  // When score prioritization is active, expand priority options to include all levels
  // because the scoring model can calculate any priority level
  if (shouldPriorityBeReadonly) {
    const allPriorityLevels = getPriorityList('Low', 'Critical', _.get(taskType, 'priorityAlias'));
    priorityListOptions = allPriorityLevels;
  }

  // When score prioritization is active, use the existing calculated priority
  // The scoring API already calculated and set the correct priority value
  const priorityVal = savedPriorityVal;

  return (
    <Form.Field>
      <>
        <label>
          {_.get(priorityAlias, 'name') ? `Priority (${_.get(priorityAlias, 'name')})` : 'Priority'}
          {attributeObj.isMandatory ? <span className="mandatory-field">*</span> : null}
        </label>

        {ATTRIBUTE_TYPE_INPUT_TYPE_MAPPING[ATTRIBUTE_TYPE.PICKLIST]({
          name: attributeObj.attributeName,
          value: priorityVal,
          options: priorityListOptions,
          placeholder: attributeObj.displayName || attributeObj.name,
          onChange: attributeOnChange,
          multiple: attributeObj.multiple,
          disabled: disabled || shouldPriorityBeReadonly,
        })}

        {shouldPriorityBeReadonly && (
          <div style={readonlyMessageStyle}>Priority is readonly when score prioritization is active.</div>
        )}
      </>
    </Form.Field>
  );
};

export default PriorityForTask;
